import React, { useEffect, useState } from 'react';
import api from '../../services/api';

interface RateLimitConfig {
  id: string;
  name: string;
  description: string;
  windowMs: number;
  maxRequests: number;
  skipSuccessfulRequests: boolean;
  skipFailedRequests: boolean;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreateRateLimitData {
  name: string;
  description: string;
  windowMs: number;
  maxRequests: number;
  skipSuccessfulRequests: boolean;
  skipFailedRequests: boolean;
  enabled: boolean;
}

interface RateLimitStats {
  totalRequests: number;
  blockedRequests: number;
  averageResponseTime: number;
  lastReset: string;
}

/**
 * Rate Limit Configuration Component
 * Allows admins to configure rate limiting rules
 */
const RateLimitConfig: React.FC = () => {
  const [configs, setConfigs] = useState<RateLimitConfig[]>([]);
  const [stats, setStats] = useState<RateLimitStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newConfig, setNewConfig] = useState<CreateRateLimitData>({
    name: '',
    description: '',
    windowMs: 900000, // 15 minutes
    maxRequests: 100,
    skipSuccessfulRequests: false,
    skipFailedRequests: false,
    enabled: true
  });

  const fetchRateLimits = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching rate limit configs...');
      const res = await api.get('/admin/rate-limits');
      console.log('Rate limit response:', res);
      
      setConfigs(res.data.data.configs || []);
      setStats(res.data.data.stats || null);
    } catch (e: any) {
      console.error('Rate limit fetch error:', e);
      setError(e?.message || 'Failed to load rate limit configurations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRateLimits();
  }, []); // Empty dependency array to run only once on mount

  const handleToggleConfig = async (configId: string, enabled: boolean) => {
    try {
      await api.post('/admin/rate-limits', {
        id: configId,
        enabled
      });
      
      // Update local state
      setConfigs(prev => prev.map(config => 
        config.id === configId ? { ...config, enabled } : config
      ));
    } catch (e: any) {
      setError(e?.message || 'Failed to update rate limit configuration');
    }
  };

  const handleCreateConfig = async () => {
    try {
      const res = await api.post('/admin/rate-limits', newConfig);
      setConfigs(prev => [...prev, res.data.data]);
      setNewConfig({
        name: '',
        description: '',
        windowMs: 900000,
        maxRequests: 100,
        skipSuccessfulRequests: false,
        skipFailedRequests: false,
        enabled: true
      });
      setShowCreateForm(false);
    } catch (e: any) {
      setError(e?.message || 'Failed to create rate limit configuration');
    }
  };

  const handleDeleteConfig = async (configId: string) => {
    if (!confirm('Are you sure you want to delete this rate limit configuration?')) {
      return;
    }

    try {
      await api.delete(`/admin/rate-limits/${configId}`);
      setConfigs(prev => prev.filter(config => config.id !== configId));
    } catch (e: any) {
      setError(e?.message || 'Failed to delete rate limit configuration');
    }
  };

  const formatWindowMs = (windowMs: number) => {
    const minutes = Math.floor(windowMs / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''}`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  };

  const getRateLimitStatus = (config: RateLimitConfig) => {
    const requestsPerMinute = (config.maxRequests / (config.windowMs / 60000));
    if (requestsPerMinute >= 100) return 'Very High';
    if (requestsPerMinute >= 50) return 'High';
    if (requestsPerMinute >= 10) return 'Medium';
    return 'Low';
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      'Very High': 'bg-red-500 text-white',
      'High': 'bg-yellow-500 text-black',
      'Medium': 'bg-blue-500 text-white',
      'Low': 'bg-green-500 text-white'
    };
    return badges[status as keyof typeof badges] || 'bg-gray-500 text-white';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Rate Limit Configuration</h2>
          <p className="text-neutral-400">Manage API rate limiting rules and policies</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="btn btn-primary px-4 py-2"
        >
          Add Rate Limit
        </button>
      </div>

      <div className="card bg-sky-900/30 border border-sky-500/60 p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-sky-500/20 text-sky-200 flex items-center justify-center font-semibold">
          ℹ
        </div>
        <div className="space-y-1">
          <p className="text-sky-100 font-semibold">Dynamic Enforcement Coming Soon</p>
          <p className="text-sm text-sky-100/80">
            Edits here are saved, but the live API still uses the global limiter until we wire configs
            into the middleware and rate-limit logging. Use this panel to prepare rules ahead of the rollout.
          </p>
        </div>
      </div>

      {error && (
        <div className="card bg-red-900/20 border border-red-500 p-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Rate Limit Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card bg-neutral-900 p-4">
            <h3 className="text-sm font-medium text-neutral-400 mb-2">Total Requests</h3>
            <p className="text-2xl font-bold text-white">{(stats.totalRequests || 0).toLocaleString()}</p>
          </div>
          <div className="card bg-neutral-900 p-4">
            <h3 className="text-sm font-medium text-neutral-400 mb-2">Blocked Requests</h3>
            <p className="text-2xl font-bold text-red-400">{(stats.blockedRequests || 0).toLocaleString()}</p>
          </div>
          <div className="card bg-neutral-900 p-4">
            <h3 className="text-sm font-medium text-neutral-400 mb-2">Avg Response Time</h3>
            <p className="text-2xl font-bold text-white">{(stats.averageResponseTime || 0).toFixed(0)}ms</p>
          </div>
          <div className="card bg-neutral-900 p-4">
            <h3 className="text-sm font-medium text-neutral-400 mb-2">Last Reset</h3>
            <p className="text-2xl font-bold text-white">
              {stats.lastReset ? new Date(stats.lastReset).toLocaleTimeString() : 'Never'}
            </p>
          </div>
        </div>
      )}

      {/* Create Rate Limit Form */}
      {showCreateForm && (
        <div className="card bg-neutral-900 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Create New Rate Limit</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-300 mb-2">Name</label>
              <input
                type="text"
                value={newConfig.name}
                onChange={(e) => setNewConfig(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white"
                placeholder="api_rate_limit"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-300 mb-2">Window (minutes)</label>
              <input
                type="number"
                value={Math.floor(newConfig.windowMs / 60000)}
                onChange={(e) => setNewConfig(prev => ({ ...prev, windowMs: parseInt(e.target.value) * 60000 }))}
                className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white"
                min="1"
                max="1440"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-300 mb-2">Max Requests</label>
              <input
                type="number"
                value={newConfig.maxRequests}
                onChange={(e) => setNewConfig(prev => ({ ...prev, maxRequests: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white"
                min="1"
                max="10000"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-300 mb-2">Description</label>
              <textarea
                value={newConfig.description}
                onChange={(e) => setNewConfig(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white"
                rows={3}
                placeholder="Describe what this rate limit applies to..."
              />
            </div>
            <div className="md:col-span-2">
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newConfig.skipSuccessfulRequests}
                    onChange={(e) => setNewConfig(prev => ({ ...prev, skipSuccessfulRequests: e.target.checked }))}
                    className="mr-2"
                  />
                  <span className="text-sm text-neutral-300">Skip successful requests</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newConfig.skipFailedRequests}
                    onChange={(e) => setNewConfig(prev => ({ ...prev, skipFailedRequests: e.target.checked }))}
                    className="mr-2"
                  />
                  <span className="text-sm text-neutral-300">Skip failed requests</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newConfig.enabled}
                    onChange={(e) => setNewConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                    className="mr-2"
                  />
                  <span className="text-sm text-neutral-300">Enabled by default</span>
                </label>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-4 mt-4">
            <button
              onClick={() => setShowCreateForm(false)}
              className="btn btn-outline px-4 py-2"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateConfig}
              disabled={!newConfig.name.trim()}
              className="btn btn-primary px-4 py-2 disabled:opacity-50"
            >
              Create Rate Limit
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-neutral-400">Loading rate limit configurations...</p>
        </div>
      ) : (
        <div className="card bg-neutral-900 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Rate Limit Configurations</h3>
            <span className="text-sm text-neutral-400">{configs.length} configurations</span>
          </div>

          {configs.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-neutral-400 mb-4">No rate limit configurations</p>
              <button
                onClick={() => setShowCreateForm(true)}
                className="btn btn-primary px-4 py-2"
              >
                Create First Configuration
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {configs.map((config) => (
                <div key={config.id} className="flex items-center justify-between p-4 bg-neutral-800 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h4 className="text-white font-medium">{config.name}</h4>
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(getRateLimitStatus(config))}`}>
                        {getRateLimitStatus(config)}
                      </span>
                      <span className={`px-2 py-1 text-xs rounded-full ${config.enabled ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                        {config.enabled ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-neutral-400 text-sm mt-1">{config.description}</p>
                    <p className="text-neutral-500 text-xs mt-1">
                      {config.maxRequests} requests per {formatWindowMs(config.windowMs)} • 
                      Created: {new Date(config.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={config.enabled}
                        onChange={(e) => handleToggleConfig(config.id, e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm text-neutral-300">Toggle</span>
                    </label>
                    <button
                      onClick={() => handleDeleteConfig(config.id)}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Rate Limiting Guide */}
      <div className="card bg-neutral-900 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Rate Limiting Guide</h3>
        <div className="text-sm text-neutral-400 space-y-2">
          <p>• Rate limits help prevent API abuse and ensure fair usage</p>
          <p>• Window size determines the time period for counting requests</p>
          <p>• Max requests defines how many requests are allowed per window</p>
          <p>• Skip options allow you to exclude certain request types from counting</p>
        </div>
      </div>
    </div>
  );
};

export default RateLimitConfig;
